import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
window.L = L
import "leaflet-draw"

// Fix Leaflet default icon paths broken by bundlers
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
})

export default class extends Controller {
  static targets = [
    "map", "centerLat", "centerLng", "radiusM", "polygonGeojson",
    "kindRadio", "colorInput", "shapeSummary", "radiusPill", "shapeNotice"
  ]

  static values = {
    kind:       { type: String, default: "circle" },
    existing:   { type: String, default: "" },
    defaultLat: { type: Number, default: 51.5074 },
    defaultLng: { type: Number, default: -0.1278 }
  }

  connect() {
    if (!this.hasMapTarget) return

    // Initialize Leaflet map
    this.map = L.map(this.mapTarget, {
      zoomControl: true,
      preferCanvas: true
    })

    // CartoDB Positron clean map tiles
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: "© <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors © <a href='https://carto.com/'>CARTO</a>",
      maxZoom: 19,
    }).addTo(this.map)

    // Feature group for holding user drawn geometries
    this.drawnItems = new L.FeatureGroup()
    this.map.addLayer(this.drawnItems)

    // Initial view coordinates
    const initialLat = (this.hasCenterLatTarget && this.centerLatTarget.value)
      ? parseFloat(this.centerLatTarget.value)
      : this.defaultLatValue || 51.5074

    const initialLng = (this.hasCenterLngTarget && this.centerLngTarget.value)
      ? parseFloat(this.centerLngTarget.value)
      : this.defaultLngValue || -0.1278

    this.map.setView([initialLat, initialLng], 14)

    // Setup Leaflet Draw Toolbar & event hooks
    this._setupDrawControl()
    this._renderExistingOrInitial()

    // Map draw events
    this.map.on(L.Draw.Event.CREATED, (e) => {
      this.drawnItems.clearLayers()
      const layer = e.layer
      this.drawnItems.addLayer(layer)
      this._serialize(layer, e.layerType)
      this._updateSummary()
    })

    this.map.on(L.Draw.Event.EDITED, (e) => {
      e.layers.eachLayer((layer) => {
        const type = (layer instanceof L.Circle) ? "circle" : "polygon"
        this._serialize(layer, type)
      })
      this._updateSummary()
    })

    this.map.on(L.Draw.Event.DELETED, () => {
      this._clearFields()
      this._updateSummary()
    })

    // Click on map to place circle if in circle mode and no draw tool is active
    this.map.on("click", (e) => {
      if (this.kindValue === "circle" && (!this._activeDrawHandler || !this._activeDrawHandler._enabled)) {
        this._placeCircleAt(e.latlng.lat, e.latlng.lng)
      }
    })

    // Invalidate map size after container is fully rendered in DOM
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize()
        if (this.drawnItems.getLayers().length > 0) {
          try {
            this.map.fitBounds(this.drawnItems.getBounds().pad(0.2))
          } catch (_) {}
        }
      }
    }, 150)
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  // ── User Actions ───────────────────────────────────────────────────────────

  kindChanged(event) {
    this.kindValue = event.target.value
    this._setupDrawControl()
    this._updateSummary()
    if (this.kindValue === "circle" && this.drawnItems.getLayers().length === 0) {
      const center = this.map.getCenter()
      this._placeCircleAt(center.lat, center.lng)
    }
  }

  colorChanged(event) {
    const newColor = event.target.value || "#ef4444"
    this.drawnItems.eachLayer((layer) => {
      if (layer.setStyle) {
        layer.setStyle({ color: newColor, fillColor: newColor })
      }
    })
    this._setupDrawControl()
  }

  manualCoordinateChanged() {
    if (this.kindValue !== "circle") return

    const lat = parseFloat(this.centerLatTarget.value)
    const lng = parseFloat(this.centerLngTarget.value)
    const radius = parseFloat(this.radiusMTarget.value) || 250

    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      this._placeCircleAt(lat, lng, radius, false)
      this.map.panTo([lat, lng])
    }
  }

  setRadiusPreset(event) {
    event.preventDefault()
    const radius = parseInt(event.currentTarget.dataset.radius, 10)
    if (!radius) return

    if (this.hasRadiusMTarget) {
      this.radiusMTarget.value = radius
    }

    // Update active circle layer
    const circle = this._getCircleLayer()
    if (circle) {
      circle.setRadius(radius)
      this._serialize(circle, "circle")
      this._updateSummary()
    } else {
      const center = this.map.getCenter()
      this._placeCircleAt(center.lat, center.lng, radius)
    }
  }

  dropCircleAtMapCenter(event) {
    if (event) event.preventDefault()
    this.kindValue = "circle"
    this._selectKindRadio("circle")
    const center = this.map.getCenter()
    this._placeCircleAt(center.lat, center.lng)
  }

  centerOnUserLocation(event) {
    if (event) event.preventDefault()
    if (!navigator.geolocation) {
      alert(t('javascript.controllers.geofence_draw_controller.geolocation_is_not_supported_by_your_browser'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude
        const lng = pos.coords.longitude
        this.map.flyTo([lat, lng], 15)
        if (this.kindValue === "circle") {
          this._placeCircleAt(lat, lng)
        }
      },
      (err) => {
        console.warn("Geolocation lookup error:", err.message)
        alert(t('javascript.controllers.geofence_draw_controller.could_not_access_your_location_please_check_browse'))
      },
      { enableHighAccuracy: true, timeout: 8000 }
    )
  }

  centerOnDefault(event) {
    if (event) event.preventDefault()
    const lat = this.defaultLatValue || 51.5074
    const lng = this.defaultLngValue || -0.1278
    this.map.flyTo([lat, lng], 14)
    if (this.kindValue === "circle") {
      this._placeCircleAt(lat, lng)
    }
  }

  clearShape(event) {
    if (event) event.preventDefault()
    this.drawnItems.clearLayers()
    this._clearFields()
    this._updateSummary()
  }

  startDrawPolygon(event) {
    if (event) event.preventDefault()
    this.kindValue = "polygon"
    this._selectKindRadio("polygon")
    this._setupDrawControl()

    // Trigger polygon draw tool directly
    if (this._drawControl && this._drawControl._toolbars && this._drawControl._toolbars.draw) {
      const drawToolbar = this._drawControl._toolbars.draw
      if (drawToolbar._modes && drawToolbar._modes.polygon) {
        drawToolbar._modes.polygon.handler.enable()
      }
    }
  }

  // ── Private Helpers ────────────────────────────────────────────────────────

  _selectKindRadio(kind) {
    if (this.hasKindRadioTargets) {
      this.kindRadioTargets.forEach((radio) => {
        radio.checked = (radio.value === kind)
      })
    }
  }

  get _currentColor() {
    if (this.hasColorInputTarget && this.colorInputTarget.value) {
      return this.colorInputTarget.value
    }
    return "#ef4444"
  }

  _setupDrawControl() {
    if (this._drawControl) {
      this.map.removeControl(this._drawControl)
      this._drawControl = null
    }

    const currentColor = this._currentColor

    const drawOptions = {
      position: "topright",
      draw: {
        circle: this.kindValue === "circle" ? {
          shapeOptions: {
            color: currentColor,
            fillColor: currentColor,
            fillOpacity: 0.18,
            weight: 2
          }
        } : false,
        polygon: this.kindValue === "polygon" ? {
          shapeOptions: {
            color: currentColor,
            fillColor: currentColor,
            fillOpacity: 0.18,
            weight: 2
          },
          allowIntersection: false,
          showArea: true
        } : false,
        polyline:     false,
        rectangle:    false,
        marker:       false,
        circlemarker: false,
      },
      edit: {
        featureGroup: this.drawnItems,
        remove: true
      },
    }

    try {
      this._drawControl = new L.Control.Draw(drawOptions)
      this.map.addControl(this._drawControl)
    } catch (e) {
      console.warn("Leaflet Draw control initialization warning:", e)
    }
  }

  _renderExistingOrInitial() {
    let rendered = false

    if (this.existingValue) {
      let data = null
      try { data = JSON.parse(this.existingValue) } catch (_) {}

      if (data) {
        let layer = null
        const color = data.color || this._currentColor
        const style = { color: color, fillColor: color, fillOpacity: 0.18, weight: 2 }

        if (data.kind === "circle" && data.center_lat != null && data.center_lng != null) {
          layer = L.circle([data.center_lat, data.center_lng], {
            radius: data.radius_m || 250,
            ...style
          })
          this._serialize(layer, "circle")
        } else if (data.kind === "polygon") {
          const coords = data.polygon_geojson?.coordinates || data.coordinates
          if (coords && coords[0]) {
            const latlngs = coords[0].map(([lng, lat]) => [lat, lng])
            layer = L.polygon(latlngs, style)
            this._serialize(layer, "polygon")
          }
        }

        if (layer) {
          this.drawnItems.addLayer(layer)
          rendered = true
          try {
            this.map.fitBounds(layer.getBounds().pad(0.3))
          } catch (_) {}
        }
      }
    }

    // If new circle and form has coordinates from controller or query params
    if (!rendered && this.kindValue === "circle") {
      const lat = (this.hasCenterLatTarget && this.centerLatTarget.value) ? parseFloat(this.centerLatTarget.value) : null
      const lng = (this.hasCenterLngTarget && this.centerLngTarget.value) ? parseFloat(this.centerLngTarget.value) : null
      const radius = (this.hasRadiusMTarget && this.radiusMTarget.value) ? parseFloat(this.radiusMTarget.value) : 250

      if (lat && lng) {
        this._placeCircleAt(lat, lng, radius, false)
      } else {
        // Place initial circle at default company coordinates
        const defaultLat = this.defaultLatValue || 51.5074
        const defaultLng = this.defaultLngValue || -0.1278
        this._placeCircleAt(defaultLat, defaultLng, 250, false)
      }
    }

    this._updateSummary()
  }

  _placeCircleAt(lat, lng, radius = null, fitBounds = false) {
    const currentRadius = radius || (this.hasRadiusMTarget && parseFloat(this.radiusMTarget.value)) || 250
    const color = this._currentColor

    this.drawnItems.clearLayers()

    const circle = L.circle([lat, lng], {
      radius: currentRadius,
      color: color,
      fillColor: color,
      fillOpacity: 0.18,
      weight: 2
    })

    this.drawnItems.addLayer(circle)
    this._serialize(circle, "circle")
    this._updateSummary()

    if (fitBounds) {
      try {
        this.map.fitBounds(circle.getBounds().pad(0.3))
      } catch (_) {}
    }
  }

  _getCircleLayer() {
    let found = null
    this.drawnItems.eachLayer((layer) => {
      if (layer instanceof L.Circle) found = layer
    })
    return found
  }

  _serialize(layer, layerType) {
    if (layerType === "circle" || layer instanceof L.Circle) {
      const c = layer.getLatLng()
      const radius = Math.round(layer.getRadius())
      this._setField("centerLat", c.lat.toFixed(6))
      this._setField("centerLng", c.lng.toFixed(6))
      this._setField("radiusM",   radius)
      this._setField("polygonGeojson", "")
    } else {
      const geojson = layer.toGeoJSON()
      this._setField("polygonGeojson", JSON.stringify(geojson.geometry))
      this._setField("centerLat", "")
      this._setField("centerLng", "")
      this._setField("radiusM",   "")
    }
  }

  _clearFields() {
    ["centerLat", "centerLng", "radiusM", "polygonGeojson"].forEach((name) => this._setField(name, ""))
  }

  _setField(targetName, value) {
    if (this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]) {
      const target = this[`${targetName}Target`]
      target.value = value
      target.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  _updateSummary() {
    if (!this.hasShapeSummaryTarget) return

    const layers = this.drawnItems.getLayers()
    if (layers.length === 0) {
      this.shapeSummaryTarget.innerHTML = `
        <div class="flex items-center gap-2 text-amber-700 text-xs font-medium">
          <span class="material-symbols-outlined !text-base">warning</span>
          <span>${t("javascript.controllers.geofence_draw_controller.no_boundary_drawn")}</span>
        </div>
      `
      return
    }

    const layer = layers[0]
    if (layer instanceof L.Circle) {
      const center = layer.getLatLng()
      const radius = Math.round(layer.getRadius())
      const areaHa = ((Math.PI * radius * radius) / 10000).toFixed(2)
      this.shapeSummaryTarget.innerHTML = `
        <div class="flex items-center justify-between text-xs text-gray-700 w-full">
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full" style="background-color: ${this._currentColor}"></span>
            <span class="font-bold text-gray-900">${t("javascript.controllers.geofence_draw_controller.circle_fence")}</span>
            <span>${t("javascript.controllers.geofence_draw_controller.center", { lat: center.lat.toFixed(5), lng: center.lng.toFixed(5) })}</span>
          </div>
          <div class="flex items-center gap-3">
            <span class="bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded">${t("javascript.controllers.geofence_draw_controller.radius_m", { radius: radius.toLocaleString() })}</span>
            <span class="text-gray-500 font-medium">${t("javascript.controllers.geofence_draw_controller.area_ha", { area: areaHa })}</span>
          </div>
        </div>
      `
    } else if (layer instanceof L.Polygon) {
      const latlngs = layer.getLatLngs()[0] || []
      this.shapeSummaryTarget.innerHTML = `
        <div class="flex items-center justify-between text-xs text-gray-700 w-full">
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full" style="background-color: ${this._currentColor}"></span>
            <span class="font-bold text-gray-900">${t("javascript.controllers.geofence_draw_controller.polygon_fence")}</span>
            <span>${t("javascript.controllers.geofence_draw_controller.boundary_vertices", { count: latlngs.length })}</span>
          </div>
          <span class="bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded">${t("javascript.controllers.geofence_draw_controller.custom_polygon_ready")}</span>
        </div>
      `
    }
  }
}
